/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    chain,
    DispatchSetUseOneTimeAddress,
    SetCurrentShipTo,
    SetShipToForEditing,
    ValidateShippingAddress,
} from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseOneTimeAddress";
import validateShippingAddressForm from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/ValidateShippingAddressForm";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { parseBoolean } from "@rpsc/XDL/Utilities";
import cloneDeep from "lodash/cloneDeep";

export const XngSetShipToForEditing = (props: any) => {
    const {
        getState,
        parameter: { useOneTimeAddress },
        cart,
    } = props;
    const shipTosParameter: GetShipTosApiParameter = {
        billToId: cart.billToId,
        expand: ["validation"],
        exclude: ["showAll"],
    };
    const state = getState() as ApplicationState;
    const shipTosForBillTo = getShipTosDataView(state, shipTosParameter)?.value || [];
    const shipToState = getShipToState(state, state.context.session.shipToId);
    props.page = getCurrentPage(state).type;

    const countries = getCurrentCountries(state);
    if (!countries) {
        throw new Error("There were no countries loaded yet");
    }

    if (useOneTimeAddress) {
        const shipTo = shipTosForBillTo.find((o: ShipToModel) => o.oneTimeAddress);
        if (!shipTo) {
            throw new Error("The one-time address shipto was not found in the cart. It must be loaded with the cart.");
        }
        props.shipToForEditing = {
            ...cloneDeep(shipTo),
            id: shipTo?.id || "",
            // XNG-Change
            country: null,
        };
        // XNG-Change Remove country selection

        // const countries = getCurrentCountries(state);
        // if (!countries) {
        //     throw new Error("There were no countries loaded yet");
        // }

        // props.shipToForEditing.country = countries.length === 1 ? countries[0] : null;
    } else {
        const state = getState() as ApplicationState;
        const {
            pages: {
                checkoutShipping: { lastSelectedShippingAddress },
                // XNG-Change
                onePageCheckout: { shippingAddressFormState },
            },
        } = state;
        const currentShipTo = shipToState.value;

        let shipTo: ShipToModel | undefined =
            props.page === "CheckoutShippingPage" ? lastSelectedShippingAddress : currentShipTo;
        if (!shipTo?.id || shipTo?.id === cart.billToId) {
            shipTo = shipTosForBillTo.find((o: ShipToModel) => o.isDefault);
            if (!shipTo) {
                shipTo = shipTosForBillTo.find(
                    (o: ShipToModel) => !o.isNew && !o.oneTimeAddress && o.id !== cart.billToId,
                );
            }
            if (!shipTo) {
                shipTo =
                    shipTosForBillTo.find(
                        (o: ShipToModel) => !o.isNew && !o.oneTimeAddress && o.id === cart.billToId,
                    ) || shippingAddressFormState?.address;
                // throw new Error(
                //     "No applicable shipto (e.g. one-time, billto) was found in the cart. At least one shipto must be available to allow editing.",
                // );
            }
        }

        props.shipToForEditing = shipTo;
    }
};

export const XngSetCurrentShipTo: typeof SetCurrentShipTo = props => {
    // XNG-Change: Handle show edit mode in case no shiptos
    if (!props.parameter.useOneTimeAddress && props.shipToForEditing.id) {
        props.dispatch(
            setCurrentShipTo({
                shipToId: props.shipToForEditing.id,
            }),
        );
    }
};

export const XNGValidateShippingAddress: typeof ValidateShippingAddress = async props => {
    // XNG-Change
    let addressFieldsDataView = getAddressFieldsDataView(props.getState());
    if (!addressFieldsDataView.value) {
        addressFieldsDataView = getAddressFieldsDataView(props.getState());
    }

    const awaitableValidate = makeHandlerChainAwaitable<Parameters<typeof validateShippingAddressForm>[0], boolean>(
        validateShippingAddressForm,
    );
    const isShippingAddressValid = await awaitableValidate({
        address: props.shipToForEditing,
        validation: props.shipToForEditing.validation!,
        fieldDisplay: addressFieldsDataView.value!.shipToAddressFields,
    })(props.dispatch, props.getState);

    const skipValidation = isShippingAddressValid || !parseBoolean(props.shipToForEditing.properties.webOnly);

    props.dispatch({
        type: "Pages/CheckoutShipping/SetIsShippingAddressUpdateRequired",
        isShippingAddressUpdateRequired: !skipValidation || props.parameter.useOneTimeAddress,
    });
    // XNG-Change
    props.dispatch({
        type: "Pages/OnePageCheckout/SetIsShippingAddressUpdateRequired",
        isShippingAddressUpdateRequired: !skipValidation || props.parameter.useOneTimeAddress,
    });
    if (skipValidation && !props.parameter.useOneTimeAddress) {
        props.dispatch({
            type: "xCheckout/SetIsEditing",
            data: { isEditing: false },
        } as any);
    } else if (props.parameter.useOneTimeAddress) {
        props.dispatch({
            type: "xCheckout/SetIsEditing",
            data: { isEditing: true },
        } as any);
    }
};

export const XngDispatchSetUseOneTimeAddress = (props: any) => {
    props.dispatch({
        type: "Pages/CheckoutShipping/SetUseOneTimeAddress",
        useOneTimeAddress: props.parameter.useOneTimeAddress,
        shipTo: props.shipToForEditing,
    });
    if (props.page === "OnePageCheckoutPage") {
        props.dispatch({
            type: "Pages/OnePageCheckout/SetShippingAddress",
            address: props.shipToForEditing,
        });
    }
};

replaceInChain(chain, SetShipToForEditing, XngSetShipToForEditing);
replaceInChain(chain, SetCurrentShipTo, XngSetCurrentShipTo);
replaceInChain(chain, ValidateShippingAddress, XNGValidateShippingAddress);
replaceInChain(chain, DispatchSetUseOneTimeAddress, XngDispatchSetUseOneTimeAddress);
